import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FiMessageCircle, FiMinus, FiSend, FiStar, FiX, FiMapPin } from "react-icons/fi";
import "../../styles/chatbot.css";
import { getApiUrl } from "../../utils/site";

// 🔥 Smart suggestions (categorized)
const baseSuggestions = [
  "Show me gemstone rings",
  "Do you offer custom jewellery?",
  "How do I place an order?",
  "What payment methods do you offer?",
];

const locationSuggestions = [
  "Where is your shop?",
  "Workshop location",
  "Mining locations",
  "Contact information",
];

const formatTime = (date) =>
  date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Welcome to Roha Jewellery ✨\nAsk me about products, custom designs, orders, or our locations.",
      time: new Date(),
    },
  ]);

  const [dynamicSuggestions, setDynamicSuggestions] = useState(baseSuggestions);

  const endRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen]);

  const statusLabel = useMemo(
    () => (loading ? "Roha assistant is typing..." : "Luxury support • Instant replies"),
    [loading]
  );

  // 🔥 Smart suggestion switcher
  const updateSuggestions = (text) => {
    const msg = text.toLowerCase();

    if (
      msg.includes("where") ||
      msg.includes("location") ||
      msg.includes("address")
    ) {
      setDynamicSuggestions(locationSuggestions);
    } else {
      setDynamicSuggestions(baseSuggestions);
    }
  };

  const sendMessage = async (customText) => {
    const text = (customText ?? input).trim();
    if (!text || loading) return;

    updateSuggestions(text);

    const userMessage = { sender: "user", text, time: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(getApiUrl("/api/chatbot/qa"), {
        message: text,
      });

      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text:
            response.data?.response ||
            "I can assist you with jewellery, orders, locations, and custom designs.",
          time: new Date(),
        },
      ]);
    } catch (error) {
      console.error("Chatbot error:", error);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "Connection issue. Please try again or contact us directly.",
          time: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  return (
    <div className="roha-chatbot" aria-live="polite">
      <button
        type="button"
        className="roha-chatbot__toggle"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? "Close chatbot" : "Open chatbot"}
      >
        {isOpen ? <FiX /> : <FiMessageCircle />}
      </button>

      {isOpen && (
        <section className="roha-chatbot__panel">
          <header className="roha-chatbot__header">
            <div>
              <p>Roha Assistant</p>
              <strong>Luxury experience, powered by AI</strong>
            </div>
            <button
              type="button"
              className="roha-chatbot__icon-btn"
              onClick={() => setIsOpen(false)}
            >
              <FiMinus />
            </button>
          </header>

          <div className="roha-chatbot__status">
            <span>
              <FiStar /> {statusLabel}
            </span>
          </div>

          {/* 🔥 Dynamic Suggestions */}
          <div className="roha-chatbot__suggestions">
            {dynamicSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => sendMessage(suggestion)}
                disabled={loading}
              >
                {suggestion}
              </button>
            ))}
          </div>

          <div className="roha-chatbot__messages">
            {messages.map((message, index) => (
              <article
                key={`${message.sender}-${index}-${message.time.getTime()}`}
                className={`roha-chatbot__bubble roha-chatbot__bubble--${message.sender}`}
              >
                <p>{message.text}</p>
                <span>{formatTime(message.time)}</span>
              </article>
            ))}

            {loading && <div className="roha-chatbot__loading">Typing...</div>}
            <div ref={endRef} />
          </div>

          <form className="roha-chatbot__form" onSubmit={handleSubmit}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about jewellery, location, orders..."
              disabled={loading}
            />
            <button type="submit" disabled={loading || !input.trim()}>
              <FiSend />
            </button>
          </form>
        </section>
      )}
    </div>
  );
};

export default React.memo(Chatbot);